import { contrastRatio, parseColor } from "./color.ts";

/** What a tenant may override: the accent per theme and the logo. */
export interface TenantTheme {
  /** A CSS colour (`#rrggbb` or `oklch(…)`) for primary actions, the current item and focus. */
  accent?: string;
  /** The accent in the dark theme; `accent` is used when absent. */
  accentDark?: string;
  /**
   * A same-origin URL for the logo slot, minted per request when a backoffice wires it. Only ASCII
   * path characters are accepted: a non-ASCII filename must arrive percent-encoded or is rejected.
   */
  logoUrl?: string;
  /** The logo in the dark theme; `logoUrl` is used when absent or when it fails to load. */
  logoUrlDark?: string;
  /** The logo's accessible name (the tenant's name). A logo applies only with a label. */
  logoLabel?: string;
}

/** The default `--accent-text` of the dark theme (tokens.css), restored when a dark accent fails. */
const DARK_ACCENT_TEXT = "oklch(0.76 0.12 275)";

/** The base surface of each theme (`--surface-base` in tokens.css). */
export const THEME_SURFACES = {
  light: "oklch(0.992 0.002 265)",
  dark: "oklch(0.175 0.006 265)",
} as const;
const AA_TEXT = 4.5;
const STYLE_ID = "esi-tenant-theme";
/** Root attributes `BrandMark` reads: `data-brand="tenant"` once the logo loaded, and its name. */
export const BRAND_ATTR = "data-brand";
export const BRAND_LABEL_ATTR = "data-brand-label";
/**
 * The characters a logo path may hold: URL path and query characters, minus everything that could
 * end the CSS string or `url()` it is written into (quotes, parentheses, backslash, whitespace).
 * The path goes into the rule as is (no re-encoding), so the URL shown is the URL preloaded.
 */
const SAFE_PATH = /^\/[\w\-.~/%!$&*+,;=:@?]*$/;

/** Bumped by every call, so a logo that loads after a newer call never applies. */
let generation = 0;

export type AccentOutcome = "applied" | "fills-only" | "default";

function accentRules(accent: string | undefined, surface: string) {
  const color = accent ? parseColor(accent) : null;
  const base = parseColor(surface);
  if (!accent || !color || !base) {
    return { rules: [] as string[], outcome: "default" as AccentOutcome };
  }
  // Text on the fill: whichever of near-white or near-black reads better.
  const onLight = contrastRatio(color, [1, 1, 1]) >= contrastRatio(color, [0.07, 0.07, 0.09]);
  const rules = [
    `--accent-solid: ${accent};`,
    `--accent-solid-hover: color-mix(in oklch, ${accent} 88%, ${onLight ? "black" : "white"});`,
    `--accent-on: ${onLight ? "#fcfcfd" : "#111216"};`,
    // Primary actions are neutral by default; a tenant accent fills them.
    "--action-solid: var(--accent-solid);",
    "--action-solid-hover: var(--accent-solid-hover);",
    "--action-on: var(--accent-on);",
  ];
  // Text and focus use the accent only where it reads on the surface; else the default stays.
  if (contrastRatio(color, base) >= AA_TEXT) {
    rules.push(`--accent-text: ${accent};`);
    return { rules, outcome: "applied" as AccentOutcome };
  }
  return { rules, outcome: "fills-only" as AccentOutcome };
}

/**
 * What {@link applyTenantTheme} does with `accent` in `theme`: `applied` (fills, text and focus),
 * `fills-only` (below 4.5:1 on the theme's surface: text and focus keep the default) or `default`
 * (no usable colour). The Branding screen states it before and after saving.
 */
export function accentOutcome(
  accent: string | null | undefined,
  theme: "light" | "dark",
): AccentOutcome {
  return accentRules(accent ?? undefined, THEME_SURFACES[theme]).outcome;
}

/**
 * Applies a tenant's overrides on the token layer, so no component changes. Each theme is checked
 * on its own: an accent below 4.5:1 against that theme's surface still fills primary actions, but
 * text and focus indicators keep the default accent. Calling it again replaces the previous
 * overrides; `{}` restores the defaults.
 *
 * The logo needs a label and a same-origin path. It is preloaded, and only once it loads does the
 * root get `data-brand="tenant"` (which swaps the ESI mark for it), `--brand-logo` its URL and
 * `--brand-logo-ratio` its intrinsic aspect ratio (so its slot ends where the logo does); a
 * dark-theme logo that loads too replaces both under `[data-theme="dark"]`. `logoLoaded` settles
 * with whether the tenant logo is now shown.
 */
export function applyTenantTheme(
  overrides: TenantTheme,
  doc: Document = document,
): { light: AccentOutcome; dark: AccentOutcome; logo: boolean; logoLoaded: Promise<boolean> } {
  const light = accentRules(overrides.accent, THEME_SURFACES.light);
  const dark = accentRules(overrides.accentDark ?? overrides.accent, THEME_SURFACES.dark);
  // `:root` rules reach dark mode too: a light accent used for text must not leak there.
  if (dark.outcome !== "applied" && light.outcome === "applied") {
    dark.rules.push(`--accent-text: ${DARK_ACCENT_TEXT};`);
  }
  const lightPath = logoPath(overrides.logoUrl, doc);
  const darkPath = logoPath(overrides.logoUrlDark, doc);
  const label = overrides.logoLabel?.trim() ?? "";
  const logo = Boolean(lightPath && label);
  const root = doc.documentElement;
  const current = ++generation;
  root.removeAttribute(BRAND_ATTR);
  root.removeAttribute(BRAND_LABEL_ATTR);

  let style = doc.getElementById(STYLE_ID);
  if (!style) {
    style = doc.createElement("style");
    style.id = STYLE_ID;
    doc.head.append(style);
  }
  const target = style;
  // The logo rules join only once their image has loaded: until then the ESI mark shows.
  const write = (lightLogo: string[], darkLogo: string[]) => {
    target.textContent = [
      `:root { ${[...light.rules, ...lightLogo].join(" ")} }`,
      `[data-theme="dark"] { ${[...dark.rules, ...darkLogo].join(" ")} }`,
    ].join("\n");
  };
  write([], []);

  let logoLoaded: Promise<boolean> = Promise.resolve(false);
  if (lightPath && logo) {
    const darkLoad = darkPath && darkPath !== lightPath ? preload(darkPath) : null;
    logoLoaded = preload(lightPath).then(async (ratio) => {
      if (ratio === null || current !== generation) {
        return false;
      }
      const lightRule = logoRule(lightPath, ratio);
      write(lightRule, []);
      root.setAttribute(BRAND_LABEL_ATTR, label);
      root.setAttribute(BRAND_ATTR, "tenant");
      // A dark logo that fails leaves the light one in both themes.
      const darkRatio = darkPath ? await darkLoad : null;
      if (darkPath && darkRatio !== null && current === generation) {
        write(lightRule, logoRule(darkPath, darkRatio));
      }
      return true;
    });
  }
  return { light: light.outcome, dark: dark.outcome, logo, logoLoaded };
}

/**
 * A logo path the app may request: same-origin and safe inside a CSS `url("…")`. Anything else
 * is dropped before an image is created, so no request ever reaches another origin.
 */
function logoPath(path: string | undefined, doc: Document): string | null {
  if (!path || !SAFE_PATH.test(path) || path.startsWith("//")) {
    return null;
  }
  const origin = doc.location.origin;
  try {
    return new URL(path, origin).origin === origin ? path : null;
  } catch {
    return null;
  }
}

/** The tenant logo slot's ratio when the image has none: the sidebar's 160 x 24 box. */
const DEFAULT_LOGO_RATIO = "20 / 3";

/**
 * The logo's custom properties. An unknown ratio (0) takes the slot's default 160 x 24 box, set
 * explicitly so a dark logo never inherits the light one's ratio.
 */
function logoRule(path: string, ratio: number): string[] {
  return [
    `--brand-logo: url("${path}");`,
    `--brand-logo-ratio: ${ratio > 0 ? Number(ratio.toFixed(4)) : DEFAULT_LOGO_RATIO};`,
  ];
}

/**
 * Resolves once the image loaded, with its intrinsic aspect ratio (0 when it has none), or `null`
 * when it failed.
 */
function preload(src: string): Promise<number | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () =>
      resolve(
        image.naturalWidth > 0 && image.naturalHeight > 0
          ? image.naturalWidth / image.naturalHeight
          : 0,
      );
    image.onerror = () => resolve(null);
    image.src = src;
  });
}
